import { useEffect, useMemo, useState } from 'react';
import { UMAccordionSection, UMButton, UMFormStyles, UMIcon, UMR } from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import { Modal } from '../proposition-c/ui';
import { paramsComplete, useImpact } from './hooks';
import { perimeterValid, StepPerimeter, type PerimeterValue } from './StepPerimeter';
import { StepHypotheses } from './StepHypotheses';
import { StepTeam } from './StepTeam';
import type { AoParams, Bootstrap, PlanProject, TeamMember, Variants } from './types';

const STEPS = ['Périmètre', 'Hypothèses de l’AO', 'Équipe'];

const PROGRESS_STEPS = [
  'Copie des bâtiments du périmètre',
  'Reprise de la traçabilité UrbanMap',
  'Calcul des consommations selon les hypothèses',
  'Création du projet',
];

function StepHeader({ index, variantForm }: { index: number; variantForm: 'A' | 'B' }) {
  if (variantForm === 'B') return null;
  return (
    <ol style={{ listStyle: 'none', display: 'flex', gap: 8, margin: '0 0 20px', padding: 0 }}>
      {STEPS.map((s, i) => (
        <li key={s} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8, borderBottom: `3px solid ${i <= index ? PLAN.accent : UMR.border}` }}>
          <span
            style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0, fontSize: 12, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: i <= index ? PLAN.accent : UMR.grey100, color: i <= index ? UMR.white : UMR.textSecondary,
            }}
          >
            {i + 1}
          </span>
          <span style={{ fontSize: 13, fontWeight: i === index ? 700 : 400, color: i <= index ? UMR.textStrong : UMR.textSecondary }}>{s}</span>
        </li>
      ))}
    </ol>
  );
}

function Progress({ value }: { value: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 0' }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>Constitution de la base de travail…</div>
      <div style={{ height: 6, background: UMR.grey100 }}>
        <div style={{ height: '100%', width: `${value}%`, background: PLAN.accent, transition: 'width 200ms' }} />
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {PROGRESS_STEPS.map((s, i) => {
          const done = value >= (i + 1) * 25;
          return (
            <li key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: done ? UMR.textStrong : UMR.textSecondary }}>
              <UMIcon name={done ? 'check_circle' : 'radio_button_unchecked'} size={18} color={done ? PLAN.accent : UMR.textSecondary} />
              {s}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function CreateProjectWizard({
  data, variants, onCancel, onCreated,
}: {
  data: Bootstrap;
  variants: Variants;
  onCancel: () => void;
  onCreated: (project: PlanProject) => void;
}) {
  const owner = data.candidates.find((c) => c.owner) ?? data.candidates[0];
  const [step, setStep] = useState(0);
  const [perimeter, setPerimeter] = useState<PerimeterValue>({
    name: 'AO Gentilly — réseau de chaleur',
    kind: 'commune',
    perimeterId: data.perimeters[0].id,
  });
  const [params, setParams] = useState<AoParams>(data.defaultParams);
  const [members, setMembers] = useState<TeamMember[]>([owner]);
  const [progress, setProgress] = useState<number | null>(null);

  const impact = useImpact(data.geo, params);
  const okPerimeter = perimeterValid(perimeter, data.perimeters);
  const okParams = paramsComplete(params);
  const inviteDuringCreation = variants.invite === 'A';
  const lastStep = inviteDuringCreation ? 2 : 1;
  const selected = data.perimeters.find((p) => p.id === perimeter.perimeterId);

  useEffect(() => {
    if (progress === null) return undefined;
    if (progress >= 100) {
      const t = window.setTimeout(() => onCreated({
        id: `pl-${Date.now()}`,
        name: perimeter.name.trim(),
        perimeterLabel: selected?.label ?? '',
        territoryKind: perimeter.kind,
        buildingCount: selected?.buildingCount ?? 0,
        createdAt: data.lineage.extractedAt,
        members,
        params,
      }), 300);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setProgress((p) => Math.min(100, (p ?? 0) + 20)), 220);
    return () => window.clearTimeout(t);
  }, [progress, perimeter, params, members, selected, data.lineage.extractedAt, onCreated]);

  const canCreate = okPerimeter && okParams;
  const blockReason = useMemo(() => {
    if (!okPerimeter) return 'Renseignez un nom et un territoire contenant des bâtiments.';
    if (!okParams) return 'Les hypothèses de l’appel d’offres sont obligatoires pour créer le projet.';
    return '';
  }, [okPerimeter, okParams]);

  const stepPerimeter = <StepPerimeter value={perimeter} onChange={setPerimeter} perimeters={data.perimeters} lineage={data.lineage} />;
  const stepHypotheses = (
    <StepHypotheses
      value={params}
      onChange={(v) => setParams(v)}
      stations={data.weatherStations}
      years={data.weatherYears}
      impact={impact}
      impactVariant={variants.impact}
      ready={okParams}
    />
  );
  const stepTeam = <StepTeam candidates={data.candidates} members={members} onChange={setMembers} />;

  const body = variants.form === 'B'
    ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <UMAccordionSection title="1 · Périmètre" defaultOpen>{stepPerimeter}</UMAccordionSection>
        <UMAccordionSection title="2 · Hypothèses de l’appel d’offres" defaultOpen>{stepHypotheses}</UMAccordionSection>
        {inviteDuringCreation && <UMAccordionSection title="3 · Équipe">{stepTeam}</UMAccordionSection>}
      </div>
    )
    : (
      <>
        <StepHeader index={step} variantForm={variants.form} />
        {step === 0 && stepPerimeter}
        {step === 1 && stepHypotheses}
        {step === 2 && stepTeam}
      </>
    );

  const wizardFooter = (
    <>
      {step > 0 && <UMButton variant="secondary" onClick={() => setStep(step - 1)}>Précédent</UMButton>}
      {step < lastStep
        ? <UMButton onClick={() => setStep(step + 1)} disabled={step === 0 && !okPerimeter}>Suivant</UMButton>
        : <UMButton icon="content_copy" onClick={() => setProgress(0)} disabled={!canCreate}>Créer le projet</UMButton>}
    </>
  );

  return (
    <Modal title="Nouveau projet Plan" onClose={progress !== null ? () => undefined : onCancel} width={880} footer={progress !== null ? undefined : (
      <>
        <span style={{ marginRight: 'auto', fontSize: 12, color: UMR.textSecondary, maxWidth: 420 }}>{blockReason}</span>
        <UMButton variant="secondary" onClick={onCancel}>Annuler</UMButton>
        {variants.form === 'B'
          ? <UMButton icon="content_copy" onClick={() => setProgress(0)} disabled={!canCreate}>Créer le projet</UMButton>
          : wizardFooter}
      </>
    )}>
      <UMFormStyles />
      {progress !== null ? <Progress value={progress} /> : body}
    </Modal>
  );
}
